import { useEffect, useState } from "react";
import AppShell from "../../components/AppShell";
import { PageHeader, Panel, EmptyState } from "../../components/Bits";
import { Modal } from "./Students";
import { api, formatApiError } from "../../lib/api";
import { Plus, LogOut as Out } from "lucide-react";
import { toast } from "sonner";

export default function AdminVisitors() {
  const [rows, setRows] = useState(null);
  const [students, setStudents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ visitor_name: "", relation: "", student_id: "", purpose: "", id_proof: "" });
  const [saving, setSaving] = useState(false);

  async function load() {
    const [v, s] = await Promise.all([api.get("/admin/visitors"), api.get("/admin/students")]);
    setRows(v.data); setStudents(s.data);
  }
  useEffect(() => { load(); }, []);

  async function save(e) {
    e.preventDefault(); setSaving(true);
    try {
      await api.post("/admin/visitors", form);
      toast.success("Visitor logged");
      setOpen(false); setForm({ visitor_name: "", relation: "", student_id: "", purpose: "", id_proof: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  }

  async function markOut(v) {
    await api.put(`/admin/visitors/${v.id}`, { out_time: new Date().toISOString() });
    toast.success("Marked checked-out"); load();
  }

  return (
    <AppShell>
      <PageHeader
        overline="Front desk"
        title="Visitor log"
        subtitle="A quiet record of who came, who they met, and when they left."
        actions={
          <button onClick={() => setOpen(true)} data-testid="add-visitor-btn" className="bg-terracotta text-white px-5 py-2.5 rounded-md text-sm inline-flex items-center gap-2"><Plus className="h-4 w-4" /> Log visitor</button>
        }
      />

      <Panel>
        {rows === null ? <div className="text-sm text-warm">Loading…</div>
          : rows.length === 0 ? <EmptyState title="No visitors logged yet" hint="When someone visits a resident, log them here." />
            : (
              <div className="overflow-x-auto -mx-6">
                <table className="min-w-full text-sm">
                  <thead><tr className="bg-[#F4F1EA] text-warm uppercase tracking-wider text-xs">
                    <th className="text-left px-6 py-3">Visitor</th>
                    <th className="text-left px-6 py-3">Meeting</th>
                    <th className="text-left px-6 py-3">Relation</th>
                    <th className="text-left px-6 py-3">In</th>
                    <th className="text-left px-6 py-3">Out</th>
                    <th className="text-right px-6 py-3">Action</th>
                  </tr></thead>
                  <tbody>
                    {rows.map((v) => (
                      <tr key={v.id} className="border-t border-warm hover:bg-[#FDFBF7]">
                        <td className="px-6 py-3"><div className="font-medium text-deep">{v.visitor_name}</div><div className="text-xs text-warm">{v.purpose || ""}</div></td>
                        <td className="px-6 py-3">{v.student_name}</td>
                        <td className="px-6 py-3 capitalize">{v.relation}</td>
                        <td className="px-6 py-3">{v.in_time ? new Date(v.in_time).toLocaleString() : "—"}</td>
                        <td className="px-6 py-3">{v.out_time ? new Date(v.out_time).toLocaleString() : <span className="text-[#8C6D31]">still inside</span>}</td>
                        <td className="px-6 py-3 text-right">
                          {!v.out_time && <button onClick={() => markOut(v)} data-testid={`checkout-visitor-${v.id}`} className="text-sm inline-flex items-center gap-1 text-[#C86F58] hover:underline"><Out className="h-4 w-4" /> Check out</button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
      </Panel>

      {open && (
        <Modal title="Log a visitor" onClose={() => setOpen(false)}>
          <form onSubmit={save} className="grid sm:grid-cols-2 gap-4">
            <label className="block sm:col-span-2"><span className="text-xs uppercase tracking-wider text-deep font-medium mb-1.5 block">Meeting resident *</span>
              <select required value={form.student_id} onChange={(e) => setForm({ ...form, student_id: e.target.value })} className={input} data-testid="visitor-student-select">
                <option value="">Select…</option>
                {students.map((s) => <option key={s.id} value={s.id}>{s.name} — Room {s.room_number || "—"}</option>)}
              </select>
            </label>
            <FieldL label="Visitor name *"><input required value={form.visitor_name} onChange={(e) => setForm({ ...form, visitor_name: e.target.value })} className={input} data-testid="visitor-name" /></FieldL>
            <FieldL label="Relation *"><input required value={form.relation} onChange={(e) => setForm({ ...form, relation: e.target.value })} placeholder="father, friend, sister…" className={input} /></FieldL>
            <FieldL label="ID proof"><input value={form.id_proof} onChange={(e) => setForm({ ...form, id_proof: e.target.value })} placeholder="Aadhar last 4 digits…" className={input} /></FieldL>
            <FieldL label="Purpose"><input value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} className={input} /></FieldL>
            <div className="sm:col-span-2 flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)} className="px-5 py-2 rounded-md text-sm border border-warm">Cancel</button>
              <button disabled={saving} type="submit" data-testid="save-visitor-btn" className="bg-terracotta text-white px-6 py-2 rounded-md text-sm disabled:opacity-60">{saving ? "Saving…" : "Log"}</button>
            </div>
          </form>
        </Modal>
      )}
    </AppShell>
  );
}

const input = "w-full px-3 py-2 rounded-md border border-warm bg-white focus:outline-none focus:ring-2 focus:ring-[#E8C2B8] focus:border-[#C86F58] text-sm";
function FieldL({ label, children }) { return <label className="block"><span className="text-xs uppercase tracking-wider text-deep font-medium mb-1.5 block">{label}</span>{children}</label>; }
